import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
} from '@angular/core';
import {
	FormsModule,
	ReactiveFormsModule,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import {
	MatDialog,
	MatDialogRef,
	MatDialogState,
} from '@angular/material/dialog';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import { debounce, mergeMap, tap } from 'rxjs/operators';
import { emptyUser, PhoneNumber, User } from 'src/app/models/data/user';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatPrefix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatAutocomplete,
	MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/select';
import { PhoneTypeFormComponent } from '../phones-form/phone-type-form.component';
import { AsyncPipe } from '@angular/common';

@Component({
	selector: 'app-user-search-bar',
	templateUrl: './user-search-bar.component.html',
	styleUrls: [
		'./user-search-bar.component.scss',
		'../../ui/common/main.scss',
		'../../ui/common/stepper.scss',
	],
	imports: [
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		MatAutocompleteTrigger,
		MatIcon,
		MatPrefix,
		MatAutocomplete,
		MatOption,
		FormsModule,
		ReactiveFormsModule,
		PhoneTypeFormComponent,
		AsyncPipe,
	],
})
export class UserSearchBarComponent {
	emailSearchEvents: BehaviorSubject<string> = new BehaviorSubject<string>('');
	inputChangeEvents: BehaviorSubject<User> = new BehaviorSubject<User>(
		emptyUser()
	);
	userSearchResults: Observable<any>;
	isOracle = false;

	cancelOrConfirmDialogRef?: MatDialogRef<CancelConfirmationComponent>;

	@Input() searchAllUsers = false;
	@Input() headerText: string =
		$localize`:@@userSearchBar-addUserHeader:Add User`;
	@Input() descriptionText: string =
		$localize`:@@userSearchBar-addUserMessageNonOracle:Input a new User's information`;

	@ViewChild('filterInput') public userInput!: ElementRef;
	@ViewChild('filterList') public filterList!: ElementRef;

	@Output() selectedUserEvents = new EventEmitter<SelectedUserEvent>();

	user: User = emptyUser();
	userType: UserType = UserType.newUser;
	email = new UntypedFormControl({ disabled: this.textEntryDisabled }, [
		Validators.email,
		Validators.required,
	]);
	firstName = new UntypedFormControl({ disabled: this.textEntryDisabled }, [
		Validators.required,
	]);
	lastName = new UntypedFormControl({ disabled: this.textEntryDisabled }, [
		Validators.required,
	]);

	lastSearchedEmail = '';

	get textEntryDisabled(): boolean {
		return this.userType === UserType.existingUser;
	}

	constructor(
		private readonly apiService: ApiService,
		private readonly dialog: MatDialog,
		private scService: SystemCapabilityService
	) {
		this.isOracle = this.scService.isOracle;

		if (this.isOracle) {
			this.descriptionText = $localize`:@@userSearchBar-addUserMessageOracle:Search for an existing User by email or input a new User's information`;
		}

		this.userSearchResults = this.emailSearchEvents.asObservable().pipe(
			tap((it) => {
				if (it.length === 0) {
					this.user = emptyUser();
					this.userType = UserType.newUser;

					this.emitUser();
				}
			}),
			debounce(() => interval(300)),
			mergeMap((email) => {
				if (email.length < 2) {
					return of([]);
				}

				const noResultsText = $localize`:@@userSearchBar-noResults:No results found for '${email}' Please enter a different search criteria.`;

				const queryParameters: QueryParam[] = [
					{ param: 'email', value: email },
					{ param: 'rowIndex', value: 0 },
					{ param: 'rowCount', value: 100 },
				];
				if (this.searchAllUsers) {
					return this.apiService.getAllUsers(queryParameters).then((users) => {
						if (users.length === 0) {
							return [noResultsText];
						}

						return users;
					});
				} else {
					return this.apiService.getMyUsers(queryParameters).then((users) => {
						if (users.length === 0) {
							return [noResultsText];
						}

						return users;
					});
				}
			})
		);
		this.inputChangeEvents.pipe(debounce(() => interval(500))).subscribe(() => {
			this.syncNameAndEmailControlDisabledState();

			this.emitUser();
		});
	}

	displayUserEmail(user: User): string {
		return user?.email ?? '';
	}

	displayUserSearchItem(item: any): string {
		return item?.email ?? item;
	}

	onUserSelected(user: any): void {
		if (user.id !== undefined) {
			this.user = user;
			this.userType = UserType.existingUser;
			this.firstName.setValue(user.firstName ?? '');
			this.lastName.setValue(user.lastName ?? '');
			this.syncNameAndEmailControlDisabledState();

			this.emitUser();
		}
	}

	onUserInputChange(): void {
		this.inputChangeEvents.next(this.user);
	}

	onEmailBlur(): void {
		void this.checkForExactEmailMatch();
	}

	showExistingUserDialog(user: User): void {
		if (
			!this.cancelOrConfirmDialogRef ||
			this.cancelOrConfirmDialogRef.getState() !== MatDialogState.OPEN
		) {
			this.cancelOrConfirmDialogRef = this.dialog.open(
				CancelConfirmationComponent,
				{
					panelClass: 'styled-dialog-container',
					data: {
						dialogHeader: $localize`:@@userSearchBar-confirmUserDlgTitle:Warning`,
						dialogContent: $localize`:@@userSearchBar-confirmUserDlgMessage:A user with email ${user.email} already exists. Use this existing user?`,
						dialogPrimaryButtonText: $localize`:@@userSearchBar-confirmUserDlgConfirmBtn:Confirm`,
						dialogSecondaryButtonText: $localize`:@@userSearchBar-confirmUserDlgCancelBtn:Cancel`,
						primaryFunction: () => {
							this.onUserSelected(user);
						},
					},
				}
			);
		}
	}

	updateUserPhone(phoneNumber: PhoneNumber): void {
		// we want to set the individual values and NOT create a new phone number object every time to avoid triggering ngOnChanges
		// on the phone number component
		if (!this.user.phoneNumber) {
			this.user.phoneNumber = {};
		}
		this.user.phoneNumber.countryCode = phoneNumber.countryCode;
		this.user.phoneNumber.rawPhoneNumber = phoneNumber.rawPhoneNumber;
	}

	private emitUser(): void {
		this.selectedUserEvents.emit({
			userType: this.userType,
			user: this.user,
			emailValid: this.email.valid,
		});
	}

	public clear(): void {
		const currentCountryCode = this.user.phoneNumber?.countryCode;
		this.user = emptyUser();

		// this is to clear out the phone number field. We'll keep the curretnly selected country code and
		// use a single space for the phone number (if it was the empty string it would skip updating the
		// phone number so it wouldn't get cleared)
		this.user.phoneNumber = {
			countryCode: currentCountryCode,
			rawPhoneNumber: ' ',
		};
		this.userType = UserType.newUser;
		this.lastSearchedEmail = '';
		this.email.setValue('');
		this.firstName.setValue('');
		this.lastName.setValue('');
		this.syncNameAndEmailControlDisabledState();

		this.userInput.nativeElement.value = '';
	}

	private async checkForExactEmailMatch(): Promise<void> {
		this.syncNameAndEmailControlDisabledState();

		const normalizedEmail = this.normalizeEmail(this.user.email);
		if (
			normalizedEmail === '' ||
			!this.email.valid ||
			normalizedEmail === this.lastSearchedEmail
		) {
			this.emitUser();
			return;
		}

		this.lastSearchedEmail = normalizedEmail;
		const queryParameters: QueryParam[] = [
			{ param: 'email', value: normalizedEmail },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		const users = await this.apiService.getAllUsers(queryParameters);
		const existingUser = users.find(
			(user) => this.normalizeEmail(user.email) === normalizedEmail
		);
		if (existingUser) {
			this.showExistingUserDialog(existingUser);
			return;
		}

		this.emitUser();
	}

	private normalizeEmail(email?: string): string {
		return email?.trim().toLowerCase() ?? '';
	}

	// Added new function for a sync name & email control disabled state check :
	private syncNameAndEmailControlDisabledState(): void {
		if (this.textEntryDisabled) {
			this.email.disable();
			this.firstName.disable();
			this.lastName.disable();
		} else {
			this.email.enable();
			this.firstName.enable();
			this.lastName.enable();
		}
	}
}

export enum UserType {
	newUser,
	existingUser,
}

export interface SelectedUserEvent {
	userType: UserType;
	user: User;
	emailValid: boolean;
}
